import { useDispatch } from 'react-redux';
import { extensionUtils, firebaseUtils, logger } from '@/utils';
import { CustomizedBlockPage, IExtensionInfo, IFlagsConfig } from '@/models';
import {
  setCustomizedBlockPage,
  setExtensionInfo,
  updateFlagsConfig,
  setRedirectUrl,
  setWhitelist,
  IWhitelistState,
} from '@/store/reducers';
import { EXTENSION_MESSAGE_TYPES } from '@/constants/external/extension-message-types';
import { LOGGER_FEATURE, LOGGER_ACTION } from '@/constants/logger/logger-tags';
import { EXTENSION_UPDATE_CONFIG_EVENT } from '@/constants/external/extension';
import { isVerifiedExtensionEvent } from '@/utils/helpers/extension-events';

const EXTENSION_LOGOUT_TIMEOUT_MS = 5000;
const EXTENSION_CONFIG_REFRESH_TIMEOUT_MS = 5000;

// module level so updates from every hook instance share one order
let configUpdateQueue: Promise<void> = Promise.resolve();

// a failed check is swallowed here with no error popup, so null would leave guarded pages blank
const EXTENSION_INFO_CHECK_FAILED: IExtensionInfo = {
  extensionVersion: '',
  isInstalled: false,
  installedTime: 0,
};

export interface IDataFromExtension {
  config: IFlagsConfig;
  customizedBlockPage: CustomizedBlockPage;
  backgroundImageInByte: string;
  redirectUrl?: string | null;
  whitelist?: IWhitelistState;
}

// different keys that extension return under config
// can be found under SHARED_DATA_WITH_WEB in extension
const RELEVANT_EXTENSION_CHANGE_KEYS = [
  'config',
  'customizedBlockPage',
  'backgroundImageInByte',
  'redirectUrl',
  'whitelist',
];

/**
 * Hook for managing extension information using Redux
 */
export const useExtension = () => {
  const dispatch = useDispatch();

  const loadDataFromExtension = async () => {
    try {
      const extensionData =
        await extensionUtils.sendMessageToExtension<IDataFromExtension>(
          EXTENSION_MESSAGE_TYPES.GET_CONFIG
        );
      if (extensionData?.config) {
        // consent-derived flags are server-authoritative; the extension's stale copies
        // must not clobber the server-synced values, so drop them before merging.
        // isPrivacyAgreed and mandatoryButtonClicked mirror the consent write and are
        // dropped for the same reason. onNewOptInSchema is intentionally kept: the
        // extension is its source and the web-app only reads it.
        const {
          optInPreferences: _optInIgnored,
          eulaAgreed: _eulaIgnored,
          isPrivacyAgreed: _isPrivacyAgreedIgnored,
          mandatoryButtonClicked: _mandatoryButtonClickedIgnored,
          sawBucketsOptInOnboardingScreen: _sawIgnored,
          reconsentBannerDismissed: _reconsentIgnored,
          consentAffiliates: _affiliatesIgnored,
          consentThirdParties: _thirdPartiesIgnored,
          ...configFromExtension
        } = extensionData.config;
        dispatch(updateFlagsConfig(configFromExtension));
      }
      if (
        extensionData?.customizedBlockPage ||
        extensionData?.backgroundImageInByte
      ) {
        dispatch(
          setCustomizedBlockPage({
            ...extensionData.customizedBlockPage,
            ...(extensionData.backgroundImageInByte && {
              backgroundImageInByte: extensionData.backgroundImageInByte,
            }),
          })
        );
      }
      if (extensionData) {
        dispatch(setRedirectUrl(extensionData.redirectUrl ?? null));
      }
      if (extensionData?.whitelist) {
        dispatch(setWhitelist(extensionData.whitelist));
      }
    } catch (error) {
      logger.error('Failed to load data from extension', error, {
        tags: {
          feature: LOGGER_FEATURE.EXTENSION,
          action: LOGGER_ACTION.CONFIG_UPDATE,
        },
      });
    }
  };

  const init = async () => {
    try {
      const extensionInfo: IExtensionInfo =
        await extensionUtils.getExtensionInfo();
      dispatch(setExtensionInfo(extensionInfo));
      // only load config if extension is installed, otherwise skip to avoid timeout delay
      if (extensionInfo.isInstalled) {
        await loadDataFromExtension();
      }
    } catch (error) {
      dispatch(setExtensionInfo(EXTENSION_INFO_CHECK_FAILED));
      logger.error('Failed to initialize extension', error, {
        tags: {
          feature: LOGGER_FEATURE.EXTENSION,
          action: LOGGER_ACTION.INIT,
        },
      });
    }

    const handler = (e: CustomEventInit) => {
      if (isVerifiedExtensionEvent(e)) {
        const keysFromPing = (e.detail.changedKeys || []) as string[];
        const hasRelevantChange = keysFromPing.some(key =>
          RELEVANT_EXTENSION_CHANGE_KEYS.includes(key)
        );

        if (!hasRelevantChange) return;
        loadDataFromExtension();
      } else {
        logger.warn('Received extension update from non-verified source', {
          tags: {
            feature: LOGGER_FEATURE.EXTENSION,
            action: LOGGER_ACTION.CONFIG_UPDATE,
            security: 'unverified',
          },
        });
      }
    };

    document.addEventListener(EXTENSION_UPDATE_CONFIG_EVENT, handler);
    return () =>
      document.removeEventListener(EXTENSION_UPDATE_CONFIG_EVENT, handler);
  };

  /**
   * Pushes the current Firebase access token to the extension so it can stay in sync.
   */
  const sendAccessTokenToExtension = async () => {
    const currentUser = firebaseUtils.getCurrentUser();
    if (!currentUser) return;

    const token = await firebaseUtils.getUserToken();
    const uid = firebaseUtils.getUserId();
    if (!token) {
      logger.error('Failed to get user token for extension', undefined, {
        tags: {
          feature: LOGGER_FEATURE.EXTENSION,
          action: LOGGER_ACTION.SEND_TOKEN,
        },
        extra: { userId: uid },
      });
      return;
    }
    await extensionUtils.sendMessageToExtension(
      EXTENSION_MESSAGE_TYPES.LOGIN_WITH_CUSTOM_TOKEN,
      {
        accessToken: token,
        uid: uid,
      }
    );
  };

  const updateFlagConfigInExtension = async (
    flagsConfigToUpdate: Partial<IFlagsConfig>
  ) => {
    await extensionUtils.sendMessageToExtension(
      EXTENSION_MESSAGE_TYPES.UPDATE_FLAGS_CONFIG_FROM_WEB,
      { ...flagsConfigToUpdate }
    );
  };

  const logoutExtension = async () => {
    await extensionUtils.sendMessageToExtension(
      EXTENSION_MESSAGE_TYPES.LOGOUT,
      undefined,
      EXTENSION_LOGOUT_TIMEOUT_MS
    );
  };

  /**
   * Notifies the extension that config was updated on the server.
   * The extension will then fetch the latest config from the server by itself.
   * This is used for anonymous users since they don't have Firestore sync.
   */
  const notifyExtensionConfigChanged = async () => {
    await extensionUtils.sendMessageToExtension(
      EXTENSION_MESSAGE_TYPES.SYNC_CONFIG_TRIGGERED_FROM_WEB
    );
  };

  const refreshExtensionConfigFromServer = async (): Promise<void> => {
    const isRefreshed = await extensionUtils.sendMessageToExtension<boolean>(
      EXTENSION_MESSAGE_TYPES.SYNC_CONFIG_TRIGGERED_FROM_WEB,
      undefined,
      EXTENSION_CONFIG_REFRESH_TIMEOUT_MS
    );
    if (isRefreshed === true) return;
    logger.warn('Extension did not confirm the config refresh from server', {
      tags: {
        feature: LOGGER_FEATURE.EXTENSION,
        action: LOGGER_ACTION.CONFIG_UPDATE,
      },
    });
  };

  // the extension pushes its whole stored groups list to the server on this update,
  // so it reloads them first or a stale list would overwrite a recent group change
  const sendConfigUpdateToExtension = async (
    configUpdate: Pick<IDataFromExtension, 'redirectUrl' | 'whitelist'>,
    failureMessage: string
  ) => {
    try {
      await refreshExtensionConfigFromServer();
      await extensionUtils.sendMessageToExtension(
        EXTENSION_MESSAGE_TYPES.UPDATE_CONFIG_FROM_WEB,
        configUpdate
      );
    } catch (error) {
      logger.error(failureMessage, error, {
        tags: {
          feature: LOGGER_FEATURE.EXTENSION,
          action: LOGGER_ACTION.CONFIG_UPDATE,
        },
        extra: configUpdate,
      });
    }
  };

  // queued so a slow refresh cannot let an older update reach the extension after a newer one
  const enqueueConfigUpdateToExtension = (
    configUpdate: Pick<IDataFromExtension, 'redirectUrl' | 'whitelist'>,
    failureMessage: string
  ): Promise<void> => {
    configUpdateQueue = configUpdateQueue.then(() =>
      sendConfigUpdateToExtension(configUpdate, failureMessage)
    );
    return configUpdateQueue;
  };

  const updateRedirectUrlInExtension = async (redirectUrl: string | null) => {
    dispatch(setRedirectUrl(redirectUrl));
    await enqueueConfigUpdateToExtension(
      { redirectUrl },
      'Failed to update redirect URL in extension'
    );
  };

  const updateWhitelistInExtension = async (
    updatedWhitelist: IWhitelistState
  ) => {
    dispatch(setWhitelist(updatedWhitelist));
    await enqueueConfigUpdateToExtension(
      { whitelist: updatedWhitelist },
      'Failed to update whitelist in extension'
    );
  };

  // one message, so the extension never keeps a redirect to a site the new whitelist blocks
  const clearRedirectAndUpdateWhitelistInExtension = async (
    updatedWhitelist: IWhitelistState
  ) => {
    dispatch(setRedirectUrl(null));
    dispatch(setWhitelist(updatedWhitelist));
    await enqueueConfigUpdateToExtension(
      { redirectUrl: null, whitelist: updatedWhitelist },
      'Failed to clear redirect URL and update whitelist in extension'
    );
  };

  return {
    init,
    sendAccessTokenToExtension,
    logoutExtension,
    updateFlagConfigInExtension,
    notifyExtensionConfigChanged,
    updateRedirectUrlInExtension,
    updateWhitelistInExtension,
    clearRedirectAndUpdateWhitelistInExtension,
  };
};
